import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { useAIState } from '../../contexts/AIStateContext';
import { Button } from '../ui/button';
import { AlertTriangle, Loader2, CheckCircle2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

// Surfaces leads the AI never scored (credit-cap "black hole") and lets the
// user bulk-approve every Bronze/paused lead in one click. Both are driven by
// additive backend endpoints — no change to existing pipeline behaviour.
export const BackfillBanner = ({ onChanged }) => {
  const { planAiEnabled, aiStatusLoaded } = useAIState();
  const [status, setStatus] = useState(null);
  const [approving, setApproving] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('backfill_banner_dismissed') === '1'; } catch { return false; }
  });

  const dismiss = () => {
    try { sessionStorage.setItem('backfill_banner_dismissed', '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/api/leads/backfill-status');
      setStatus(data || null);
    } catch (_e) {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  const approveAllBronze = async () => {
    setApproving(true);
    try {
      const { data } = await api.post('/api/leads/approve-all-bronze');
      toast.success(data?.message || 'Bronze leads approved — Brain will resume outreach.');
      onChanged && onChanged();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve Bronze leads.');
    } finally {
      setApproving(false);
    }
  };

  if (dismissed) return null;
  // Manual Pro (no-AI plan): there is no AI scoring, so hide the notice entirely.
  if (aiStatusLoaded && planAiEnabled === false) return null;
  if (!status || (status.pending_unscored || 0) <= 0) return null;

  const { pending_unscored, affordable_now, needs_topup, bronze_pending } = status;
  const hasBronze = (bronze_pending || 0) > 0;

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700/60 dark:bg-amber-950/30"
      data-testid="backfill-banner"
    >
      <div className="flex items-start gap-2.5 flex-1">
        <AlertTriangle className="h-4.5 w-4.5 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
        <div className="text-sm text-amber-900 dark:text-amber-200">
          <span className="font-semibold" data-testid="backfill-pending-count">{pending_unscored}</span>{' '}
          lead{pending_unscored === 1 ? '' : 's'} are waiting for AI scoring —
          {' '}the Brain will process{' '}
          <span className="font-medium">{affordable_now}</span>{' '}
          automatically as credits allow (contactable-first).
          {needs_topup && (
            <span className="block text-xs mt-0.5 text-amber-700 dark:text-amber-300">
              Add credits to score the rest — none are dropped, they stay queued.
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {hasBronze && (
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
            onClick={approveAllBronze}
            disabled={approving}
            data-testid="approve-all-bronze-btn"
            title={`${bronze_pending} Bronze lead(s) are held for review — approve to let the Brain resume outreach`}
          >
            {approving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
            Approve {bronze_pending} Bronze
          </Button>
        )}
        {needs_topup && (
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => (window.location.href = '/billing')}
            data-testid="backfill-topup-btn"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Add credits
          </Button>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="text-amber-600/70 hover:text-amber-800 dark:text-amber-400/70 dark:hover:text-amber-200 transition-colors shrink-0 p-0.5"
          data-testid="backfill-dismiss-btn"
          title="Dismiss for this session"
          aria-label="Dismiss AI scoring notice"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default BackfillBanner;
